if (!customElements.get('sam-story-video')) {
  customElements.define('sam-story-video', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const on = (target, event, handler) => target.addEventListener(event, handler, { signal: this.abort.signal });
      this.video = this.querySelector('video');
      this.progress = this.querySelector('[data-film-progress]');
      this.volume = this.querySelector('[data-film-volume]');
      this.mute = this.querySelector('[data-film-mute]');
      this.fullscreen = this.querySelector('[data-film-fullscreen]');
      this.status = this.querySelector('[role="status"]');
      this.toggles = [...this.querySelectorAll('[data-film-toggle]')];
      this.querySelector('[aria-label="Film controls"]').setAttribute('aria-label', 'Video controls');
      this.progress.setAttribute('aria-label', 'Seek through video');
      this.toggles.forEach(button => on(button, 'click', () => this.toggle()));
      on(this.video, 'click', () => {
        if (matchMedia('(pointer: coarse)').matches && !this.classList.contains('controls-visible')) this.showControls();
        else this.toggle();
      });
      on(this, 'pointermove', () => this.showControls());
      on(this, 'pointerdown', () => { this.interacting = true; this.showControls(); });
      on(window, 'pointerup', () => { if (this.interacting) { this.interacting = false; this.showControls(); } });
      on(window, 'pointercancel', () => { this.interacting = false; this.showControls(); });
      on(this, 'focusin', () => this.showControls());
      on(this, 'focusout', () => this.showControls());
      on(this.video, 'play', () => {
        this.classList.add('is-started');
        document.querySelectorAll('sam-story-video video').forEach(video => { if (video !== this.video) video.pause(); });
        this.syncPlayback();
      });
      ['pause', 'ended'].forEach(event => on(this.video, event, () => this.syncPlayback()));
      ['timeupdate', 'loadedmetadata', 'durationchange'].forEach(event => on(this.video, event, () => this.syncTime()));
      on(this.video, 'error', () => this.message('This video couldn’t load. Please try again.'));
      on(this.progress, 'input', () => {
        if (Number.isFinite(this.video.duration)) {
          this.video.currentTime = Math.min(this.video.duration, Math.max(0, Number(this.progress.value)));
          this.syncTime();
          this.showControls();
        }
      });
      on(this.mute, 'click', () => {
        this.video.muted = !(this.video.muted || this.video.volume === 0);
        if (!this.video.muted && this.video.volume === 0) this.video.volume = this.lastVolume || 1;
        this.syncVolume();
      });
      on(this.volume, 'input', () => {
        this.video.volume = Number(this.volume.value);
        this.video.muted = this.video.volume === 0;
        if (this.video.volume > 0) this.lastVolume = this.video.volume;
        this.syncVolume();
      });
      on(this.video, 'volumechange', () => this.syncVolume());
      on(this.fullscreen, 'click', () => this.toggleFullscreen());
      ['fullscreenchange', 'webkitfullscreenchange'].forEach(event => on(document, event, () => this.syncFullscreen()));
      on(this.video, 'webkitbeginfullscreen', () => this.syncFullscreen(true));
      on(this.video, 'webkitendfullscreen', () => this.syncFullscreen(false));
      on(document, 'visibilitychange', () => { if (document.hidden) this.video.pause(); });
      this.observer?.disconnect();
      this.observer = new IntersectionObserver(entries => {
        if (!entries[0].isIntersecting || entries[0].intersectionRatio < .1) this.video.pause();
      }, { threshold: [0, .1] });
      this.observer.observe(this);
      const deviceVolume = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      this.volume.hidden = deviceVolume;
      this.syncTime();
      this.syncVolume();
      this.syncPlayback();
      this.video.controls = false;
      this.classList.add('is-enhanced');
    }
    async toggle() {
      if (!this.video.paused && !this.video.ended) { this.video.pause(); return; }
      this.status.hidden = true;
      if (this.video.error) this.video.load();
      if (this.video.ended) this.video.currentTime = 0;
      try { await this.video.play(); }
      catch (error) { if (error.name !== 'AbortError') this.message('Press play to try again.'); }
    }
    syncPlayback() {
      const playing = !this.video.paused && !this.video.ended;
      this.toggles.forEach(button => {
        button.setAttribute('aria-label', playing ? 'Pause video' : this.video.ended ? 'Replay video' : 'Play video');
        button.querySelector('[data-play-icon]').hidden = playing;
        button.querySelector('[data-pause-icon]').hidden = !playing;
      });
      this.showControls();
    }
    showControls() {
      clearTimeout(this.hideTimer);
      this.classList.add('controls-visible');
      if (this.video.paused || this.video.ended) return;
      this.hideTimer = setTimeout(() => {
        const focused = document.activeElement;
        if (!this.interacting && !(this.contains(focused) && focused.matches(':focus-visible')) && !this.video.paused) this.classList.remove('controls-visible');
      }, 1800);
    }
    time(seconds) { return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`; }
    syncTime() {
      const duration = Number.isFinite(this.video.duration) ? this.video.duration : 0;
      const current = this.video.currentTime || 0;
      this.progress.disabled = duration <= 0;
      this.progress.max = duration || 100;
      this.progress.value = current;
      this.progress.style.setProperty('--range-fill', `${duration ? current / duration * 100 : 0}%`);
      this.progress.setAttribute('aria-valuetext', `${this.time(current)} of ${this.time(duration)}`);
      this.querySelector('[data-film-elapsed]').textContent = this.time(current);
      this.querySelector('[data-film-duration]').textContent = this.time(duration);
    }
    syncVolume() {
      const muted = this.video.muted || this.video.volume === 0;
      const value = muted ? 0 : this.video.volume;
      this.mute.classList.toggle('is-muted', muted);
      this.mute.setAttribute('aria-pressed', String(muted));
      this.mute.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
      this.volume.value = value;
      this.volume.style.setProperty('--range-fill', `${value * 100}%`);
      this.volume.setAttribute('aria-valuetext', `${Math.round(value * 100)}%`);
    }
    async toggleFullscreen() {
      try {
        if (document.fullscreenElement) await document.exitFullscreen();
        else if (document.webkitFullscreenElement) document.webkitExitFullscreen();
        else if (this.requestFullscreen) await this.requestFullscreen();
        else if (this.webkitRequestFullscreen) this.webkitRequestFullscreen();
        else if (this.video.webkitEnterFullscreen) this.video.webkitEnterFullscreen();
        else this.message('Fullscreen isn’t available in this browser.');
      } catch { this.message('Fullscreen isn’t available in this browser.'); }
    }
    syncFullscreen(native = false) {
      const active = document.fullscreenElement === this || document.webkitFullscreenElement === this || native;
      this.fullscreen.setAttribute('aria-label', active ? 'Exit fullscreen' : 'Enter fullscreen');
      this.fullscreen.setAttribute('aria-pressed', String(active));
      this.showControls();
    }
    message(text) { this.status.textContent = text; this.status.hidden = false; this.showControls(); }
    disconnectedCallback() {
      this.video?.pause();
      this.abort?.abort();
      this.observer?.disconnect();
      clearTimeout(this.hideTimer);
    }
  });
}
